import { createFileRoute, notFound } from "@tanstack/react-router";
import { setSeoPageCache } from "@/lib/page-cache";
import { getCompetitorPageBySlug } from "@/lib/competitor-seo-public.functions";
import {
  CompetitorPageShell,
  type CompetitorPageData,
  type CompetitorProvider,
} from "@/components/competitor/CompetitorPageShell";

const SITE = "https://heallexa.com";

export const Route = createFileRoute("/alternatives/$")({
  loader: async ({ params }) => {
    await setSeoPageCache();
    const slug = params._splat ?? "";
    const data = await getCompetitorPageBySlug({ data: { slug } });
    if (!data.page) throw notFound();
    const page = data.page as unknown as CompetitorPageData & {
      title: string | null;
      meta_description: string | null;
      is_indexable: boolean;
    };
    return {
      slug,
      page,
      providers: (data.providers ?? []) as unknown as CompetitorProvider[],
    };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "Heallexa" }] };
    const { page, slug } = loaderData;
    const url = `${SITE}/alternatives/${slug}`;
    return {
      meta: [
        { title: page.title ?? page.h1 ?? "Heallexa" },
        { name: "description", content: page.meta_description ?? "" },
        {
          name: "robots",
          content: page.is_indexable ? "index, follow" : "noindex, follow",
        },
        { property: "og:title", content: page.title ?? page.h1 ?? "Heallexa" },
        { property: "og:description", content: page.meta_description ?? "" },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: AlternativePage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 text-center">
      <div>
        <p className="text-[13px] uppercase tracking-[0.15em] text-muted-foreground mb-4">404</p>
        <h1 className="text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">
          Page not found
        </h1>
        <p className="mt-3 text-[14px] text-muted-foreground">This comparison page does not exist.</p>
      </div>
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 text-center">
      <div>
        <h1 className="text-[clamp(1.8rem,3vw,2.5rem)] font-medium tracking-[-0.03em] leading-[1.15] text-foreground">
          Something went wrong
        </h1>
        <p className="mt-3 text-[14px] text-muted-foreground">{error.message}</p>
      </div>
    </div>
  ),
});

function AlternativePage() {
  const { page, providers } = Route.useLoaderData();
  return <CompetitorPageShell page={page} providers={providers} />;
}

